<template>
    <div id="s3files" class="fc__view files__view">
        <div class="team__intro">
            <h3>{{ trans('myfiles.title') }}</h3>
        </div>
        <div class="view__intro">
            <div class="intro__summary">
                <p v-html="trans('myfiles.description')"></p>
            </div>
            <div class="banner__missing missing__flag--red">
                <img src="/images/warning.svg" class="warning__icon">
                <div class="flex-element flex-row flex-vcenter flex-row-wrap">
                    <p class="title"><span class="title--emp">{{ trans('myfiles.warning.beware') }}</span></p>
                    <p class="subtitle" v-html="trans('myfiles.warning.text', {hours: vuedata.time_limit_hours})"></p>
                </div>
            </div>
        </div>

        <div v-if="loading" class="flex-element flex-row flex-fullcenter">
            <loader :dim="60"></loader>
        </div>
        <div v-else>
            <div v-if="files.length > 0">
                <main>
                    <header>
                        <p v-if="!search.length" v-html="trans_choice('myfiles.list_all', files)"></p>
                        <p v-else v-html="trans_choice('myfiles.files_match', filteredFiles)"></p>
                        <div id="bm-search-bar" class="input-group " style="">
                            <div class="bm-input bm-input--blank">
                                <a class="teaser">
                                    <i class="icon-bc-finder" aria-hidden="true"></i>
                                </a>
                                <a class="trigger__clear">
                                    <i class="icon-bc-cross-raw"></i>
                                </a>
                            </div>
                            <input
                                v-model="search"
                                type="text" class="form-control filter" autofocus="autofocus"
                                :placeholder="trans('myfiles.search_for')"
                            >
                        </div>
                    </header>
                </main>

                <div v-if="filteredFiles.length > 0" class="view__table">
                    <table>
                        <thead>
                            <tr>
                                <th>{{ trans('myfiles.reference') }}</th>
                                <th>{{ trans('myfiles.date') }}</th>
                                <th>{{ trans('myfiles.format') }}</th>
                                <th>{{ trans('myfiles.size') }}</th>
                                <th>{{ trans('myfiles.actions') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="file in pagination.files">
                                <td><p>{{ file.name }}</p></td>
                                <td><p><span>{{ formatDate(file.date) }}</span>{{ formatYear(file.date ) }}</p></td>
                                <td><p>{{ formatExtension(file.name) }}</p></td>
                                <td><p>{{ humanFileSize(file.size) }}</p> </td>
                                <td>
                                    <div v-if="file.link" class="links">
                                        <a :href="file.link" class="link--globe">
                                            <ArrowDown :size="12"/>
                                        </a>
                                        <a href="#" @click.prevent="file.copyLink()" class="link--globe">
                                            <Copy :size="12"/>
                                            <p :class="{'copied': file.copying}">{{ trans('myfiles.copied') }}</p>
                                        </a>
                                    </div>
                                    <div v-else-if="file.generatingLink">
                                        <loader :dim="20"></loader>
                                    </div>
                                    <div v-else class="link--plain">
                                        <a href="#" @click.prevent="file.generateLink()">{{ trans('myfiles.generate_link') }}  </a>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-else class="filtering__zero__view">
                    <div class="zero__inner">
                    <div class="img__cont">
                        <img src="/images/icon_finder_big.svg">
                            <div>
                                <p class="title">{{trans('utils.zero_filter_title')}}</p>
                                <p class="subtitle">{{trans('utils.zero_filter_subtitle')}}</p>
                            </div>
                    </div>
                    </div>
                </div>

                <div v-if="pagination.numpages > 1" class="pagination">
                    <a v-if="pagination.has_prev" @click.prevent="pagination.page--" href="#"> &lt; </a>
                    <span v-else class="noselect"> &lt; </span>
                    {{ pagination.page+1 }}/{{pagination.numpages}}
                    <a v-if="pagination.has_next" @click.prevent="pagination.page++" href="#"> &gt; </a>
                    <span v-else class="noselect"> &gt; </span>
                </div>
            </div>
            <div v-else class="bcw zero__state">
                <div class="zero__reports__content">
                    <div class="zero__inner">
                        <div class="img__cont">
                            <img src="/images/icon_folder.svg"/>
                            <div>
                                <p class="title">{{trans('myfiles.zero_title')}}</p>
                                <p class="subtitle">{{trans('myfiles.zero_sub')}}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped >

    .noselect {
        user-select: none;
    }

</style>


<script>

    // Max number of files per page
    const FILES_PER_PAGE = 10;

    // Import the copyToClipboard function
    import copyToClipboard from '../../../../../common/assets/js/tsutils/copy2clipboard';
    import {ArrowDown, Copy} from "lucide-vue-next";

    /**
     * A single file
     */
    class S3File {

        // constructor
        constructor(s3file) {

            this.key = s3file.key;
            this.name = s3file.name;
            this.size = s3file.size;
            this.date = s3file.date;
            this.link = null;
            this.generatingLink = false;
            this.copying = false;
        }

        async generateLink(){

            this.generatingLink = true;

            const data = {
                key: this.key,
            }
            const response = await axios.post('/myfiles/api/getlink', data);

            await sleep(2000);

            this.link = response.data.link;
            this.generatingLink = false;

        }

        async copyLink(){
            copyToClipboard(this.link);
            this.copying = true;
            await sleep(500);
            this.copying = false;
        }

    }

    /**
     * Retuns a promise that awaits in miliseconds
     * @param ms
     * @returns {Promise<unknown>}
     */
    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }


    export default {
        components:{
          ArrowDown,
          Copy
        },

        props: {
            vuedata: {},
        },

        data(){

            return {

                loading: true,
                files: [],
                pagination: {
                    files: [],
                    page: 0,
                    numpages: 0,
                    has_prev: false,
                    has_next: false,
                },
                search: '',
                filteredFiles: [],

            };

        },

        mounted(){

            this.loadList();

        },

        watch: {

            'pagination.page': function(){

                this.update_pagination();

            },

            search(){

                this.pagination.page = 0;
                this.update_pagination();

            }

        },

        methods: {

            /**
             * Load the inital list of files
             * @returns {Promise<void>}
             */
            async loadList(){
                const response = await axios.get('/myfiles/api/list');
                this.files = response.data.map(s3file => new S3File(s3file));
                this.update_pagination();

                await sleep(2000);
                this.loading = false;

            },

            /**
             * Update the pagination using the search and the current page number
             */
            update_pagination(){

                const search = this.search.trim().toLowerCase();
                if(!search){
                    this.filteredFiles = this.files;
                } else {
                    this.filteredFiles = this.files.filter(f => f.name.toLowerCase().indexOf(search) !== -1);
                }

                const np = Math.ceil(this.filteredFiles.length / FILES_PER_PAGE);
                this.pagination.numpages = np;

                const p = this.pagination.page;

                this.pagination.files = this.filteredFiles.slice(p * FILES_PER_PAGE, (p+1) * FILES_PER_PAGE);

                this.pagination.has_next = p < (np-1);
                this.pagination.has_prev = p !== 0;
            },

            // https://stackoverflow.com/questions/10420352/converting-file-size-in-bytes-to-human-readable-string
            humanFileSize(size) {
                var i = Math.floor( Math.log(size) / Math.log(1024) );
                return ( size / Math.pow(1024, i) ).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
            },

            formatDate(date) {
                return moment(date).format('DDMMM')
            },

            formatYear(date) {
                return moment(date).format('YYYY')
            },

            formatExtension(file) {
                return file.substring(file.lastIndexOf('.') + 1, file.length).toUpperCase()
            }



        }

    }

</script>
